import React, { useState, useEffect, useMemo, useDeferredValue } from "react"
import styles from "../../asset/styles/places/places.module.scss"
import Table, { TableColumn } from "../../component/table/table"
import utilStyles from "../../asset/styles/util/util.module.scss"
import IconsColor from "util/iconsColor"
import { agent } from "apiClient/api"
import { PlaceData } from "../../models"
import moment from "moment"
import CustomizedMenu from "component/muiMenu/muiMenu"
import { useHotelGeneric } from "../../context/HotelGenericContext"
import { useSnackbar } from "../../context/snackbarContext"
import Loader from "../../component/loader/loaderComponent"
import PlacesPopup from "./placesPopup"
import MainTabsTableHeader, { FilterGroup } from "../../component/mainTabsTableHeader/mainTabsTableHeader"
import { applyFilters, applySearch, cycleSort, sortRowsBy, uniqueOptionsFromRows } from "../../util/listFilters"
import { useListView, LIST_VIEW_KEYS } from "../../context/listFilterContext"
import { toTitleCase } from "util/textcase"
import { TablePaginationBar, useClientListPagination } from "../../component/tablePagination"

const Places: React.FC = () => {
    const { showError, showSuccess } = useSnackbar()
    const [places, setPlaces] = useState<PlaceData[]>([])
    const {
        search: searchValue, setSearch: setSearchValue,
        sortState, setSortState,
        filters: tableSelectedFilters, setFilters: setTableSelectedFilters,
        page, setPage, pageSize, setPageSize,
    } = useListView(LIST_VIEW_KEYS.places)
    const handleColumnSort = React.useCallback(
        (key: string) => setSortState((prev) => cycleSort(prev, key)),
        [setSortState],
    )
    const [popupOpen, setPopupOpen] = useState(false)
    const [popupMode, setPopupMode] = useState<"add" | "update" | "status" | "delete">("add")
    const [selectedPlace, setSelectedPlace] = useState<PlaceData | null>(null)
    const [placeUsageData, setPlaceUsageData] = useState<{ day_trip_name: string }[]>([])
    const [placeUsageLoading, setPlaceUsageLoading] = useState(false)
    const { setHotelGenericData } = useHotelGeneric()

    const GetPlaceList = async () => {
        try {
            const response = await agent.PlaceListAPI.get()
            if (response?.status === true) {
                setPlaces(response?.data ?? [])
            } else {
                showError(response?.error?.message || "Failed to load places")
            }
        } catch (error) {
            console.log("GetPlaceList Error", error)
            showError("Failed to load places")
        }
    }

    const GetGenericHotelDetails = async () => {
        try {
            const response = await agent.GenericGlobalAPI.get()
            if (response?.status === false) {
                console.log("Failed to fetch generic hotel details", response?.error)
            } else {
                setHotelGenericData(response?.data[0])
            }
        } catch (error) {
            console.log("GetGenericHotelDetails Error", error)
        }
    }

    useEffect(() => {
        ;(async () => {
            await Promise.all([GetPlaceList(), GetGenericHotelDetails()])
        })()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const columns: TableColumn<PlaceData>[] = [
        {
            key: "place_name",
            label: "Place Name",
            width: "12%",
            align: "left",
        },
        {
            key: "city_name",
            label: "City",
            width: "12%",
            align: "left",
            render: (value: string) => (
                <span className={styles.locationCell}>
                    <IconsColor iconName="location" width="16" height="16" fillColor="none" strokeColor="#7a7a7a" />
                    {value}
                </span>
            ),
        },
        {
            key: "place_type_name",
            label: "Place Type",
            width: "12%",
            align: "left",
        },
        {
            key: "flag_active",
            label: "Status",
            width: "12%",
            align: "left",
            render: (value: boolean) => (
                <span
                    className={
                        value === true
                            ? utilStyles.statusBadgeActive
                            : value === false
                              ? utilStyles.statusBadgeInactive
                              : utilStyles.statusBadgeInactive
                    }
                >
                    {value ? "Active" : "Inactive"}
                </span>
            ),
        },
        {
            key: "updated_at_timestamp",
            label: "Last Updated",
            width: "12%",
            align: "left",
            render: (value: string) => (value ? moment(value).format("DD-MMM-YYYY hh:mm A") : "-"),
        },
        {
            key: "updated_by_user_name",
            label: "Editor",
            width: "12%",
            align: "left",
        },
        {
            key: "actions",
            label: "",
            width: "5%",
            align: "left",
            render: (_: unknown, row: PlaceData) => (
                <CustomizedMenu
                    buttonText=""
                    menuItems={[
                        {
                            label: "Edit",
                            onClick: () => {
                                setSelectedPlace(row)
                                setPopupMode("update")
                                setPopupOpen(true)
                            },
                        },
                        {
                            label: `Set Status to ${row.flag_active ? "Inactive" : "Active"}`,
                            onClick: () => handleSetStatusClick(row),
                        },
                        {
                            label: "Delete",
                            onClick: () => handleDeleteClick(row),
                        },
                    ]}
                    buttonVariant="text"
                    buttonStyle={{ padding: "0px", minWidth: "0px" }}
                    buttonEndIcon={
                        <IconsColor iconName="moreVert" width="20" height="20" fillColor="none" strokeColor="#7a7a7a" />
                    }
                />
            ),
        },
    ]

    // ----- Filter accessors (City, Type, Editor, Status per spec) -----
    const FILTER_ACCESSORS = useMemo(
        () => ({
            city: (row: PlaceData) => row.city_name,
            type: (row: PlaceData) => row.place_type_name,
            editor: (row: PlaceData) => row.updated_by_user_name,
            status: (row: PlaceData) => (row.flag_active ? "Active" : "Inactive"),
        }),
        [],
    )

    const filterGroups: FilterGroup[] = useMemo(
        () => [
            { key: "city", label: "City", options: uniqueOptionsFromRows(places, FILTER_ACCESSORS.city) },
            { key: "type", label: "Type", options: uniqueOptionsFromRows(places, FILTER_ACCESSORS.type) },
            { key: "editor", label: "Editor", options: uniqueOptionsFromRows(places, FILTER_ACCESSORS.editor) },
            {
                key: "status",
                label: "Status",
                options: [
                    { label: "Active", value: "Active" },
                    { label: "Inactive", value: "Inactive" },
                ],
            },
        ],
        [places, FILTER_ACCESSORS],
    )

    // Defer heavy table re-render so clearing search/filters doesn't block the UI.
    const deferredSearch = useDeferredValue(searchValue)
    const deferredFilters = useDeferredValue(tableSelectedFilters)
    const filteredPlaces = useMemo(() => {
        let rows = applyFilters(places, deferredFilters, FILTER_ACCESSORS)
        rows = applySearch(rows, deferredSearch, [(r) => r.place_name, (r) => r.city_name])
        rows = sortRowsBy(rows, sortState)
        return rows.map((value) => {
            return {
                ...value,
                place_name: toTitleCase(value.place_name),
                city_name: toTitleCase(value.city_name),
                place_type_name: toTitleCase(value.place_type_name)
            }
        })
    }, [places, deferredFilters, deferredSearch, sortState, FILTER_ACCESSORS])

    const paginationQueryKey = JSON.stringify({ search: searchValue, filters: tableSelectedFilters, sortState })
    const {
        pagedRows: pagedPlaces,
        total: pagedTotal,
        page: currentPage,
        pageSize: currentPageSize,
        handlePageChange,
        handlePageSizeChange,
    } = useClientListPagination(filteredPlaces, {
        page, pageSize, setPage, setPageSize, queryKey: paginationQueryKey,
    })

    const handleSetStatusClick = (place: PlaceData) => {
        setSelectedPlace(place)
        setPopupMode("status")
        setPopupOpen(true)
    }

    const handleDeleteClick = (place: PlaceData) => {
        setSelectedPlace(place)
        setPopupMode("delete")
        setPlaceUsageLoading(true)
        agent.CheckPlaceUsageAPI.post({ place_id: place.place_id }, true)
            .then((response) => {
                if (response?.status === true && Array.isArray(response?.data)) {
                    setPlaceUsageData(response.data)
                } else {
                    setPlaceUsageData([])
                }
            })
            .catch(() => setPlaceUsageData([]))
            .finally(() => {
                setPlaceUsageLoading(false)
                setPopupOpen(true)
            })
    }

    const handleConfirmStatusChange = async () => {
        const placeToUpdate = selectedPlace
        setPopupOpen(false)
        setSelectedPlace(null)
        setPopupMode("add")
        if (!placeToUpdate?.place_id) return
        try {
            const response = await agent.DeletePlaceAPI.put({
                place_id: placeToUpdate.place_id,
                flag_active: !placeToUpdate.flag_active,
                place_flag_deleted: false,
            })
            if (response?.status === true) {
                showSuccess("Place status updated successfully")
                GetPlaceList()
            } else {
                showError(response?.error?.message ?? "Failed to update place status")
            }
        } catch (err) {
            console.error("Update place status error", err)
            showError("Failed to update place status")
        }
    }

    const handleConfirmDelete = async () => {
        const placeToDelete = selectedPlace
        setPopupOpen(false)
        setSelectedPlace(null)
        setPopupMode("add")
        if (!placeToDelete?.place_id) return
        try {
            const response = await agent.DeletePlaceAPI.put({
                place_id: placeToDelete.place_id,
                flag_active: true,
                place_flag_deleted: true,
            })
            if (response?.status === true) {
                showSuccess("Place deleted successfully")
                GetPlaceList()
            } else {
                showError(response?.error?.message ?? "Failed to delete place")
            }
        } catch (err) {
            console.error("Delete place error", err)
            showError("Failed to delete place")
        }
    }

    const handleClosePopup = () => {
        setPopupOpen(false)
        setSelectedPlace(null)
        setPlaceUsageData([])
        setPopupMode("add")
    }

    return (
        <>
            {placeUsageLoading && <Loader message="Checking usage..." overlay={true} />}
        <div className={`${utilStyles.pageContainer} ${utilStyles.listPageFill}`}>
            <div className={utilStyles.listPageHeader}>
                    <MainTabsTableHeader
                        searchValue={searchValue}
                        onSearchChange={setSearchValue}
                        searchPlaceholder="Search places"
                        totalCount={places.length}
                        filteredCount={filteredPlaces.length}
                        itemLabel="places"
                        sortValue=""
                        onSortChange={() => undefined}
                        sortOptions={[]}
                        filterGroups={filterGroups}
                        selectedFilters={tableSelectedFilters}
                        onFiltersChange={setTableSelectedFilters}
        addButtonLabel="Place"
                        onAddClick={() => {
                            setSelectedPlace(null)
                            setPopupMode("add")
                            setPopupOpen(true)
                        }}
                        showResultCount={false}
                    />
            </div>
            <div className={utilStyles.listPageTableBlock}>
                <div className={utilStyles.listPageTableScroll}>
                    <Table
                        columns={columns}
                        data={pagedPlaces}
                        keyExtractor={(row: PlaceData) => row.place_id}
                        emptyMessage="No places available"
                        minWidth={1000}
                        showHeader={true}
                        fillHeight={true}
                        sortKey={sortState.key}
                        sortDirection={sortState.direction}
                        onSort={handleColumnSort}
                    />
                </div>
                <TablePaginationBar
                    total={pagedTotal}
                    page={currentPage}
                    pageSize={currentPageSize}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                />
            </div>
            <PlacesPopup
                isOpen={popupOpen}
                onClose={handleClosePopup}
                mode={popupMode}
                place={popupMode === "update" || popupMode === "status" || popupMode === "delete" ? selectedPlace : null}
                placeUsageData={placeUsageData}
                onSuccess={GetPlaceList}
                onConfirmStatus={handleConfirmStatusChange}
                onConfirmDelete={handleConfirmDelete}
            />
        </div>
        </>
    )
}

export default Places
